import type * as React from 'react'
import { useEffect } from 'react'
import { Provider } from 'react-redux'
import { Router } from 'react-router-dom' // keeping until we migrate to v6
import { CompatRouter } from 'react-router-dom-v5-compat'

import { ApolloProvider } from '@apollo/client'
import { ThemeProvider } from 'styled-components'

import { FeaturesWrapper, useSplitEnabled } from '@buffer-mono/features'
import { PopcornProvider, Sidebar } from '@buffer-mono/popcorn'

import { AccountContextProvider } from '~publish/contexts/AccountContext'
import { env } from '~publish/env'
import { client } from '~publish/legacy/apollo-client'
import GlobalStyles from '~publish/legacy/global-styles/globalStyles'
import { store, history } from '~publish/legacy/store'
import { theme } from '~publish/legacy/theme'
import { PusherContextProvider } from '~publish/services/pusher'
import '~publish/legacy/composer/composer/components/css/constants.css'

import { LegacyStateSync } from './legacy-state-sync/LegacyStateSync'
import getErrorBoundary from '../../components/ErrorBoundary'
import App from '../App'
import { MixpanelSessionReplay } from '../MixpanelSessionReplay/MixpanelSessionReplay'

import '../../components/i18n'
function getQueryParams(): URLSearchParams {
  const url = new URL(window.location.href)
  const params = new URLSearchParams(url.search)
  return params
}

const ErrorBoundary = getErrorBoundary()

const Publish = (): JSX.Element => {
  useEffect(() => {
    store.dispatch({ type: 'APP_INIT', queryParams: getQueryParams() })
  }, [])

  return (
    <ErrorBoundary>
      <FeaturesWrapper environment={env.VITE_SPLIT_ENV}>
        <MixpanelSessionReplay />
        <Provider store={store}>
          <AccountContextProvider dispatch={store.dispatch}>
            <Router history={history}>
              <CompatRouter>
                <PusherContextProvider>
                  <PopcornProviderWrapper>
                    <ThemeProvider theme={theme}>
                      <LegacyStateSync />
                      <Sidebar.Provider>
                        <App />
                      </Sidebar.Provider>
                    </ThemeProvider>
                  </PopcornProviderWrapper>
                </PusherContextProvider>
              </CompatRouter>
            </Router>
          </AccountContextProvider>
        </Provider>
      </FeaturesWrapper>
    </ErrorBoundary>
  )
}

export function PublishRoot(): JSX.Element {
  return (
    <ApolloProvider client={client}>
      <GlobalStyles />
      <Publish />
    </ApolloProvider>
  )
}

// TODO: This wrapper is necessary to have split.io properly setup to enable, the
// theme switcher, remove once the theme switcher is disabled or becomes GA
function PopcornProviderWrapper({
  children,
}: {
  children: React.ReactNode
}): JSX.Element {
  const { isEnabled: isThemeSwitcherEnabled = false } = useSplitEnabled(
    'buffer-test-theme-switcher',
  )
  return (
    <PopcornProvider
      themeSwitcherEnabled={isThemeSwitcherEnabled}
      theme="light"
      portalContainerId="portals-container"
    >
      {children}
    </PopcornProvider>
  )
}
